import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { IconName, ButtonVariant, DropdownOptionGroup, PopoverPosition, DropdownSelectionType, DropdownOptionDefault, DropdownOptionType, DropdownOption, DropdownOptionIcon } from '@yupma/ui-kit';
import { DefaultUserFilterOption, UserDocument } from 'server-model';
import { IAMService, OrganisationDataService, UserDataService } from 'src/app/services/services.module';

@Component({
  selector: 'app-user-input',
  templateUrl: './user-input.component.html',
  styleUrl: './user-input.component.scss'
})
export class UserInputComponent implements OnInit, OnChanges {
  @Input() name: string = '';
  @Input() label: string = '';
  @Input() value: string = '';
  @Input() required: boolean = false;
  @Input() hideOptional: boolean = false;
  @Input() error: boolean = false;
  @Input() disabled: boolean = false;
  @Input() inlineInfo: string = '';
  @Input() errorText: string = 'This field is required.';
  @Input() showLabel: boolean = true;
  @Input() allUsers: UserDocument[] = [];
  @Input() canMultiSelect: boolean = false;
  @Input() showCurrentUserOption: boolean = false;

  @Output() valueChange = new EventEmitter<string>();

  iconNames = IconName;
  buttonVariants = ButtonVariant;

  selectedOptionLabel: string = '';
  dropdownOptions: DropdownOptionGroup[] = [];
  filteredDropdownOptions: DropdownOptionGroup[] = [];
  isDropdownOpen: boolean = false;
  popoverPositions = PopoverPosition;
  dropdownSelectionTypes = DropdownSelectionType;
  organisationId: string = '';

  constructor(
    private iamService: IAMService,
    private organisationDataService: OrganisationDataService,
    private userDataService: UserDataService,
  ) { }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['value']) {
      // Initialize value as empty string if undefined/null
      this.value = this.value || '';
    }

    this.createDropdowns();
    this.setSelectedOptions();
  }

  async ngOnInit() {
    // Initialize value as empty string if undefined/null
    this.value = this.value || '';
  }

  private createDropdowns() {
    if (this.allUsers.length) {
      const dropdownOptions = this.allUsers.map((user) => {
        const uid = this.iamService.getUserId();
        if (uid === user.id) {
          return new DropdownOptionDefault(
            DropdownOptionType.DEFAULT,
            user.first_name + ' ' + user.last_name + ' (You)',
            user.id,
            false,
            false,
            '',
            '',
            user.email
          );
        } else {
          return new DropdownOptionDefault(
            DropdownOptionType.DEFAULT,
            user.first_name ? user.first_name + ' ' + user.last_name : user.email,
            user.id,
            false,
            false,
            '',
            '',
            user.email
          );
        }
      });

      const options: DropdownOption[] = [];

      // Add empty option only for single select
      if (!this.canMultiSelect) {
        const emptyOption = new DropdownOptionDefault(DropdownOptionType.DEFAULT, '', '');
        options.push(emptyOption);
      }

      // Add current user option if enabled
      if (this.showCurrentUserOption) {
        const currentUser = new DropdownOptionIcon(
          DropdownOptionType.DEFAULT,
          'Current user',
          DefaultUserFilterOption.CURRENT_USER,
          IconName.LIGHTNING,
          false,
          false,
          '',
          '',
          'Will use logged in user'
        );
        options.push(currentUser);
      }

      options.push(...dropdownOptions);

      const dropdownGroup = new DropdownOptionGroup('', options);
      this.dropdownOptions = [dropdownGroup];
      this.filteredDropdownOptions = [dropdownGroup];
    }
  }

  private getSelectedValues(): string[] {
    // Parse semicolon-separated values
    if (!this.value || this.value.trim() === '') {
      return [];
    }
    return this.value.split(';').map(v => v.trim()).filter(v => v !== '');
  }

  private setSelectedValues(values: string[]) {
    // Join values with semicolons
    this.value = values.join(';');
  }

  private setSelectedOptions() {
    const selectedValues = this.getSelectedValues();

    this.filteredDropdownOptions.forEach(group => {
      group.options.forEach(option => {
        let isSelected = false;

        if (this.canMultiSelect) {
          // Multi-select mode
          isSelected = selectedValues.includes(option.value);
        } else {
          // Single select mode
          isSelected = option.value === this.value;
        }

        option.selected = isSelected;

        if (isSelected && !this.canMultiSelect) {
          this.selectedOptionLabel = option.label;
        }
      });
    });

    // Update selected label for multi-select
    if (this.canMultiSelect) {
      this.updateMultiSelectLabel();
    }
  }

  private updateMultiSelectLabel() {
    const selectedValues = this.getSelectedValues();

    if (selectedValues.length === 0) {
      this.selectedOptionLabel = '';
    } else if (selectedValues.length === 1) {
      const selectedOption = this.findOptionByValue(selectedValues[0]);
      this.selectedOptionLabel = selectedOption ? selectedOption.label : '';
    } else {
      this.selectedOptionLabel = `${selectedValues.length} users selected`;
    }
  }

  private findOptionByValue(value: string): DropdownOption | null {
    for (const group of this.dropdownOptions) {
      const option = group.options.find(opt => opt.value === value);
      if (option) {
        return option;
      }
    }
    return null;
  }

  selectedOption(selectedOption: DropdownOption) {
    if (this.canMultiSelect) {
      // Multi-select mode
      const selectedValues = this.getSelectedValues();
      const index = selectedValues.indexOf(selectedOption.value);

      if (index > -1) {
        // Remove from selection
        selectedValues.splice(index, 1);
        selectedOption.selected = false;
      } else {
        // Add to selection
        selectedValues.push(selectedOption.value);
        selectedOption.selected = true;
      }

      this.setSelectedValues(selectedValues);
      this.updateMultiSelectLabel();
    } else {
      // Single select mode
      this.dropdownOptions.forEach(group => {
        group.options.forEach(option => {
          option.selected = false;
        });
      });

      selectedOption.selected = true;
      this.selectedOptionLabel = selectedOption.label;
      this.value = selectedOption.value;
    }
  }

  async toggleDropdown(state: boolean) {
    this.isDropdownOpen = state;
    this.filteredDropdownOptions = this.dropdownOptions;
    this.setSelectedOptions();

    if (state === false) {
      // Always emit string value
      this.valueChange.emit(this.value);
    }
  }

  search(query: string) {
    const lowerQuery = query.toLowerCase();

    // Filter the options based on the search query
    this.filteredDropdownOptions = this.dropdownOptions.map(group => {
      const filteredOptions = group.options.filter(option => {
        return option.label.toLowerCase().includes(lowerQuery);
      });

      return new DropdownOptionGroup(group.text, filteredOptions);
    }).filter(group => group.options.length > 0);
  }

  // Helper method to get selected user names for display
  getSelectedUserNames(): string[] {
    if (!this.canMultiSelect) return [];

    const selectedValues = this.getSelectedValues();
    return selectedValues.map(value => {
      const option = this.findOptionByValue(value);
      return option ? option.label : value;
    }).filter(name => name !== '');
  }

  // Helper method to clear all selections (useful for multi-select)
  clearSelections() {
    this.value = '';
    this.selectedOptionLabel = '';

    // Update all options to unselected
    this.dropdownOptions.forEach(group => {
      group.options.forEach(option => {
        option.selected = false;
      });
    });

    this.valueChange.emit(this.value);
  }
}